import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { TableIds } from 'src/api/types/misc';
import { LinkSvg } from 'src/assets/dynamicSvg';
import { NoData } from 'src/common';
import ExpandSection from 'src/components/ExpandSection';
import Button from 'src/components/form/Button';
import GenericTable from 'src/components/GenericTable';
import { LinkScenariosToControlModal } from 'src/components/modals';
import { useStateSelector } from 'src/redux';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import styled from 'styled-components';
import { useColumns } from './useColumns';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import { ControlQuery } from 'src/graphql/_generated';
import { ControlLinkedScenarioExpandContent } from './ControlLinkedScenarioExpandContent';

const EndDiv = styled.div`
  .link-icon {
    margin-right: 10px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .link-icon {
      margin-right: 6px;
    }
  }
`;

interface LinkedScenariosSectionProps {
  control: NonNullable<ControlQuery['control']>;
  version?: string;
}

export const LinkedScenariosSection: React.FC<LinkedScenariosSectionProps> = ({ control, version }) => {
  const { controlId } = useParams<{ controlId: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [isLinkScenarioModalOpen, setIsLinkScenarioModalOpen] = useState(false);
  const colors = useStateSelector(({ theme }) => theme.colors);

  const riskManagementPlans = control.riskManagementPlans || [];

  const { columns, GTColumnSelectAnchorExported } = useColumns({
    data: riskManagementPlans,
    controlId: controlId as string,
  });

  return (
    <>
      {isLinkScenarioModalOpen && (
        <LinkScenariosToControlModal
          controlId={controlId as string}
          isModifierStrategyType={checkIsModifierStrategy(control.strategy)}
          onClose={() => setIsLinkScenarioModalOpen(false)}
          subTitle={{
            title: 'CONTROL',
            value: control?.name || '',
          }}
        />
      )}
      <ExpandSection
        title="LINKED SCENARIOS"
        sectionId="control_linked-scenarios"
        onChange={setIsOpen}
        endComponent={
          isOpen && !version ? (
            <EndDiv>
              <Button
                primary
                onClick={() => {
                  setIsLinkScenarioModalOpen(true);
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Link scenarios',
                    tags: ['RMP', 'SCENARIO'],
                  });
                }}
                data-cy="link-scenarion-btn"
              >
                <LinkSvg className="link-icon" color={colors.btnWhite} />
                LINK SCENARIOS
              </Button>
            </EndDiv>
          ) : undefined
        }
      >
        {riskManagementPlans.length ? (
          <GenericTable
            tableId={TableIds.controlScenarios}
            data={riskManagementPlans}
            columns={columns}
            GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
            searchable={['name', 'source', 'event', 'consequence', 'expected_loss']}
            expandContent={(el) => {
              if (!el.control || !el.scenario) return null;
              return (
                <ControlLinkedScenarioExpandContent
                  controlId={controlId as string}
                  scenarioId={el.scenario.id}
                />
              );
            }}
            hideExpandContentWhenClosed
          />
        ) : (
          <NoData title="No scenarios linked" />
        )}
      </ExpandSection>
    </>
  );
};
